import { ImageWithFallback } from '@/components/ImageWithFallback';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  DashboardData,
  ShowroomScheduleItem,
  ShowroomScheduleResponse,
} from '@/services/dashboard';
import { dashboardAPI } from '@/services/dashboard';
import { branchsAPI, type CompanyBranch } from '@/services/branchs';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  DollarSign,
  Eye,
  MapPin,
  MessageCircle,
  Package,
  ShoppingBag,
  Star,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Type definitions
interface Product {
  id: string;
  name: string;
  price: string;
  category: string;
  description: string;
  image: string;
  specifications: {
    dimensions: string;
    material: string;
    colors: string[];
    warranty: string;
  };
  availability: string;
  rating: number;
  reviews: number;
  location: string;
}

// Mock data
const metricsData = [
  { name: 'Jan', visits: 20, chats: 150 },
  { name: 'Feb', visits: 32, chats: 102 },
  { name: 'Mar', visits: 58, chats: 138 },
  { name: 'Apr', visits: 67, chats: 167 },
  { name: 'May', visits: 78, chats: 178 },
  { name: 'Jun', visits: 89, chats: 189 },
];

const categoryData = [
  { name: 'Persian Rugs', value: 30, color: '#8884d8' },
  { name: 'Modern Rugs', value: 25, color: '#82ca9d' },
  { name: 'Vintage Rugs', value: 20, color: '#3f51b5' },
  { name: 'Wool Rugs', value: 15, color: '#ff7300' },
  { name: 'Other Rugs', value: 2, color: '#0088fe' },
];

// Enhanced product details data
const productDetails: Record<string, Product> = {
  'Modern Sectional Sofa': {
    id: 'PROD-001',
    name: 'Modern Sectional Sofa',
    price: '$1,299',
    category: 'Furniture',
    description:
      'Contemporary sectional sofa with premium fabric upholstery and modular design. Perfect for modern living rooms.',
    image:
      'https://images.unsplash.com/photo-1698936061086-2bf99c7b9fc5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZWN0aW9uYWwlMjBzb2ZhJTIwZnVybml0dXJlfGVufDF8fHx8MTc1Njg3NjIzMnww&ixlib=rb-4.1.0&q=80&w=1080',
    specifications: {
      dimensions: 'L: 110" × W: 88" × H: 33"',
      material: 'Premium fabric, hardwood frame',
      colors: ['Charcoal', 'Navy', 'Beige'],
      warranty: '5 years',
    },
    availability: 'In Stock',
    rating: 4.8,
    reviews: 127,
    location: 'Warehouse A, Showroom 2',
  },
  'Blue Fabric Sofa': {
    id: 'PROD-002',
    name: 'Blue Fabric Sofa',
    price: '$899',
    category: 'Furniture',
    description:
      'Comfortable 3-seater sofa in beautiful blue fabric with sturdy construction and plush cushioning.',
    image:
      'https://images.unsplash.com/photo-1565295778946-7c5eb5362215?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxibHVlJTIwZmFicmljJTIwc29mYXxlbnwxfHx8fDE3NTY4NzYyMzZ8MA&ixlib=rb-4.1.0&q=80&w=1080',
    specifications: {
      dimensions: 'L: 84" × W: 36" × H: 32"',
      material: 'Blue fabric, solid wood frame',
      colors: ['Royal Blue', 'Navy Blue'],
      warranty: '3 years',
    },
    availability: 'Low Stock (3 left)',
    rating: 4.6,
    reviews: 89,
    location: 'Showroom 1',
  },
  'Designer Table Lamp': {
    id: 'PROD-003',
    name: 'Designer Table Lamp',
    price: '$249',
    category: 'Lighting',
    description:
      'Elegant table lamp with modern design and adjustable brightness. Features premium materials and stylish finish.',
    image:
      'https://images.unsplash.com/photo-1702505973200-cce1a216f92b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxkZXNpZ25lciUyMHRhYmxlJTIwbGFtcHxlbnwxfHx8fDE3NTY4NzYyMzl8MA&ixlib=rb-4.1.0&q=80&w=1080',
    specifications: {
      dimensions: 'H: 24" × W: 12"',
      material: 'Brushed metal, fabric shade',
      colors: ['Gold', 'Silver', 'Black'],
      warranty: '2 years',
    },
    availability: 'In Stock',
    rating: 4.7,
    reviews: 56,
    location: 'Lighting Section',
  },
  'Pendant Light Set': {
    id: 'PROD-004',
    name: 'Pendant Light Set',
    price: '$399',
    category: 'Lighting',
    description:
      'Set of 3 pendant lights perfect for kitchen islands or dining areas. Modern industrial design with adjustable height.',
    image:
      'https://images.unsplash.com/photo-1721414207812-df0a2afb259d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwZW5kYW50JTIwbGlnaHQlMjBzZXQlMjBraXRjaGVufGVufDF8fHx8MTc1Njg3NjI0Mnww&ixlib=rb-4.1.0&q=80&w=1080',
    specifications: {
      dimensions: 'Each: H: 8" × W: 6"',
      material: 'Metal, glass shade',
      colors: ['Black', 'Copper', 'White'],
      warranty: '3 years',
    },
    availability: 'In Stock',
    rating: 4.9,
    reviews: 143,
    location: 'Lighting Section',
  },
  'Luxury Dining Set': {
    id: 'PROD-005',
    name: 'Luxury Dining Set',
    price: '$2,199',
    category: 'Furniture',
    description:
      'Premium 6-piece dining set with solid wood table and upholstered chairs. Perfect for formal dining rooms.',
    image:
      'https://images.unsplash.com/photo-1590676177040-258c3e1c5c0a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBkaW5pbmclMjBzZXQlMjB0YWJsZXxlbnwxfHx8fDE3NTY4NzYyNDZ8MA&ixlib=rb-4.1.0&q=80&w=1080',
    specifications: {
      dimensions: 'Table: L: 84" × W: 42" × H: 30"',
      material: 'Solid oak, premium upholstery',
      colors: ['Natural Oak', 'Dark Walnut'],
      warranty: '10 years',
    },
    availability: 'In Stock',
    rating: 4.9,
    reviews: 234,
    location: 'Dining Room Section',
  },
};

type Activity = {
  id: number | string;
  type: 'visit' | 'chat';
  customer: string;
  time: string;
  scheduledTime: string | null;
  interestedProducts: {
    name: string;
    category: string;
    price: string;
    sku: string;
    image: string;
    isStorageUrl?: boolean;
    description?: string | null;
    origin?: string | null;
    productUrl?: string | null;
  }[];
  nearestOrder: {
    id: string;
    date: string;
    amount: string;
    status: string;
  } | null;
  details: string;
  showProductMissing?: boolean;
  // Customer interest / memory fields
  roomPhoto?: string | string[] | null;
  contact?: string | null;
  preferredSize?: string | null;
  preferredColor?: string | string[] | null;
  style?: string | null;
  roomType?: string | null;
  hasPets?: boolean;
  hasChildren?: boolean;
};

export function Dashboard() {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [schedulePage, setSchedulePage] = useState(1);
  const scheduleLimit = 10;
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

  const { data: dashboardData, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboardData'],
    queryFn: dashboardAPI.getDashboardData,
  });

  const { data: myBranches = [] } = useQuery<CompanyBranch[]>({
    queryKey: ['myBranches'],
    queryFn: () => branchsAPI.getMyBranches(),
  });

  useEffect(() => {
    if (myBranches.length > 0) {
      setSelectedBranchId((prev) => prev ?? myBranches[0].id);
    }
  }, [myBranches]);

  useEffect(() => {
    setSchedulePage(1);
  }, [selectedBranchId]);

  const { data: scheduleResponse } = useQuery<ShowroomScheduleResponse>({
    queryKey: ['scheduleData', selectedBranchId, schedulePage],
    queryFn: () =>
      dashboardAPI.getScheduleData(selectedBranchId as string, {
        page: schedulePage,
        limit: scheduleLimit,
      }),
    enabled: !!selectedBranchId,
  });

  const scheduleItems: ShowroomScheduleItem[] = scheduleResponse?.data ?? [];

  const mappedScheduleActivities: Activity[] = scheduleItems.map(
    (item, index) => {
      const customerName =
        item.customerName ||
        item.widgetUser.firstName ||
        item.widgetUser.lastName ||
        item.widgetUser.contact ||
        item.contactInfo ||
        'Unknown customer';

      const contact =
        item.contactInfo ||
        item.widgetUser.contact ||
        item.widgetUser.firstName;

      const interestedProducts =
        item.scheduleProducts?.map((scheduleProduct) => {
          const product = scheduleProduct.product;
          const imageFromPrimary =
            product.images?.find((img) => img.isPrimary)?.originalUrl ||
            product.images?.find((img) => img.sortOrder === 0)?.originalUrl;
          const fallbackImage =
            product.images?.[0]?.originalUrl ||
            product.images?.[0]?.thumbnailUrl ||
            product.images?.[0]?.url ||
            '';

          const productImage = imageFromPrimary || fallbackImage || '';
          const isStorageUrl =
            !!productImage &&
            !productImage.startsWith('http://') &&
            !productImage.startsWith('https://') &&
            !productImage.startsWith('blob:') &&
            !productImage.startsWith('data:');

          return {
            name: product.name,
            category: product.type || 'N/A',
            price: '', // price is not provided in the payload
            sku: product.sku || '',
            image: productImage,
            isStorageUrl,
            description: product.description,
            origin: product.origin ?? null,
            productUrl: product.product_url ?? null,
          };
        }) ?? [];

      const detailsParts: string[] = [];
      if (item.branch?.name) {
        detailsParts.push(`Branch: ${item.branch.name}`);
      }
      if (item.bookingType) {
        detailsParts.push(`Type: ${item.bookingType}`);
      }
      if (contact) {
        detailsParts.push(`Contact: ${contact}`);
      }

      // Parse roomPhoto if it's a JSON string
      let parsedRoomPhoto: string | string[] | null = item.roomPhoto;
      if (item.roomPhoto && typeof item.roomPhoto === 'string') {
        try {
          // Try to parse if it looks like JSON
          if (
            item.roomPhoto.trim().startsWith('{') ||
            item.roomPhoto.trim().startsWith('[')
          ) {
            const parsed = JSON.parse(item.roomPhoto);
            if (Array.isArray(parsed)) {
              parsedRoomPhoto = parsed;
            } else if (typeof parsed === 'object' && parsed !== null) {
              // If it's an object with URLs, extract them
              const urls = Object.values(parsed).filter(
                (val): val is string => typeof val === 'string'
              );
              parsedRoomPhoto = urls.length > 0 ? urls : item.roomPhoto;
            }
          }
        } catch {
          // If parsing fails, use original value
          parsedRoomPhoto = item.roomPhoto;
        }
      }

      // Format scheduled time from date and time
      let scheduledTime: string | null = null;
      if (item.date && item.time) {
        try {
          const dateTime = new Date(`${item.date}T${item.time}`);
          scheduledTime = dateTime.toLocaleString();
        } catch {
          // Fallback to combining date and time as strings
          scheduledTime = `${item.date} ${item.time}`;
        }
      } else if (item.bookedTime) {
        scheduledTime = new Date(item.bookedTime).toLocaleString();
      }

      return {
        id: item.id || index,
        type: 'visit',
        customer: customerName,
        time: new Date(item.createdAt).toLocaleString(),
        scheduledTime,
        interestedProducts,
        nearestOrder: null,
        details: detailsParts.join(' • '),
        showProductMissing: interestedProducts.length === 0,
        roomPhoto: parsedRoomPhoto,
        contact,
        preferredSize: item.preferredSize || null,
        preferredColor: item.preferredColor || null,
        style: item.style || null,
        roomType: item.roomType || null,
        hasPets: item.hasPets ?? false,
        hasChildren: item.hasChildren ?? false,
      };
    }
  );

  const activitiesToRender: Activity[] =
    scheduleItems.length > 0 ? mappedScheduleActivities : [];

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="gap-0 md:gap-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Products
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-12" />
            ) : (
              <div className="">
                <div className="text-lg md:text-2xl font-bold">
                  {dashboardData?.totalProducts?.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Total products in your catalog
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="gap-0 md:gap-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Products
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-12" />
            ) : (
              <div className="">
                <div className="text-lg md:text-2xl font-bold">
                  {dashboardData?.totalPublishedProducts?.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Published and visible to customers
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="gap-0 md:gap-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-12" />
            ) : (
              <div className="">
                <div className="text-lg md:text-2xl font-bold">
                  {dashboardData?.totalWidgetUsers?.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Total users in widget
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="gap-0 md:gap-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Showroom Visits
            </CardTitle>
            <Store className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-12" />
            ) : (
              <div className="">
                <div className="text-lg md:text-2xl font-bold">
                  {dashboardData?.totalBranches?.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">Total branches</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="gap-0 md:gap-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Chats</CardTitle>
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-12" />
            ) : (
              <div className="">
                <div className="text-lg md:text-2xl font-bold">
                  {dashboardData?.totalOnlineWidgetUsers?.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  Currently online in widget
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Line Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Visits & Chats Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={metricsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="visits"
                  stroke="#8884d8"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="chats"
                  stroke="#82ca9d"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Interested Product Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({
                    name = '',
                    percent = 0,
                  }: {
                    name?: string;
                    percent?: number;
                  }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Showroom Visit Schedule */}
      <Card>
        <CardHeader>
          <CardTitle>
            Showroom Visit Schedule - Customer Interest List
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" className="space-y-2">
            {activitiesToRender.map((activity) => (
              <AccordionItem
                key={activity.id}
                value={String(activity.id)}
                className="border rounded-lg px-4 py-2"
              >
                <AccordionTrigger className="hover:no-underline py-2">
                  <div className="flex items-center justify-between w-full pr-4">
                    {/* Left side: Customer info */}
                    <div className="flex items-center gap-3">
                      {activity.type === 'visit' ? (
                        <Store className="h-4 w-4 text-blue-500 flex-shrink-0" />
                      ) : (
                        <MessageCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {activity.customer}
                        </span>
                        <Badge
                          variant="outline"
                          className="text-xs px-1.5 py-0"
                        >
                          {activity.type === 'visit' ? 'Visit' : 'Chat'}
                        </Badge>
                      </div>
                    </div>

                    {/* Right side: Quick info */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {activity.type === 'visit' && activity.scheduledTime && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{activity.scheduledTime}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <ShoppingBag className="h-3 w-3" />
                        <span>{activity.interestedProducts.length}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{activity.time}</span>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="pt-4 pb-2">
                  <div className="space-y-4">
                    {/* Details */}
                    <p className="text-sm text-muted-foreground">
                      {activity.details}
                    </p>

                    {/* User Information & Preferences */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-2 border-b">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            User Information & Preferences
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        {/* Two Column Layout: Room Images | User Information */}
                        <div className="flex flex-col md:flex-row gap-6">
                          {/* Left Side - Room Images */}
                          <div className="w-full md:w-2/5 space-y-3">
                            <p className="text-xs text-muted-foreground font-medium">
                              Room Photos
                            </p>
                            {activity.roomPhoto ? (
                              <div className="space-y-3">
                                {/* Support both single image (string) and multiple images (array) */}
                                {Array.isArray(activity.roomPhoto) ? (
                                  activity.roomPhoto.map((photo, idx) => (
                                    <div
                                      key={idx}
                                      className="w-full h-48 overflow-hidden rounded-lg border bg-muted"
                                    >
                                      <ImageWithFallback
                                        src={photo}
                                        alt={`Room photo ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ))
                                ) : (
                                  <div className="w-full h-48 overflow-hidden rounded-lg border bg-muted">
                                    <ImageWithFallback
                                      src={activity.roomPhoto as string}
                                      alt="Main room view"
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="w-full h-48 flex items-center justify-center rounded-lg border bg-muted/50">
                                <p className="text-sm text-muted-foreground">
                                  No room photos provided
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Right Side - User Information */}
                          <div className="w-full md:w-3/5 space-y-4">
                            <p className="text-xs text-muted-foreground font-medium">
                              Customer Information
                            </p>

                            {/* Contact Information */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">
                                  Contact
                                </p>
                                <p className="text-sm font-medium">
                                  {activity.contact || 'Not provided'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">
                                  Preferred Size
                                </p>
                                <p className="text-sm font-medium">
                                  {activity.preferredSize || 'Not specified'}
                                </p>
                              </div>
                            </div>

                            {/* Preferences Grid */}
                            <div>
                              <p className="text-xs text-muted-foreground mb-2">
                                Preferences
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Preferred Color
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {Array.isArray(activity.preferredColor) &&
                                    activity.preferredColor.length > 0 ? (
                                      activity.preferredColor.map(
                                        (color, idx) => (
                                          <Badge
                                            key={idx}
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            {color}
                                          </Badge>
                                        )
                                      )
                                    ) : activity.preferredColor ? (
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {activity.preferredColor}
                                      </Badge>
                                    ) : (
                                      <Badge
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        Any
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Style
                                  </p>
                                  <Badge variant="outline" className="text-xs">
                                    {activity.style || 'Any'}
                                  </Badge>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Room Type
                                  </p>
                                  <Badge variant="outline" className="text-xs">
                                    {activity.roomType || 'Not specified'}
                                  </Badge>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Has Pets
                                  </p>
                                  <Badge
                                    variant={
                                      activity.hasPets ? 'secondary' : 'outline'
                                    }
                                    className="text-xs"
                                  >
                                    {activity.hasPets ? 'Yes' : 'No'}
                                  </Badge>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">
                                    Has Children
                                  </p>
                                  <Badge
                                    variant={
                                      activity.hasChildren
                                        ? 'secondary'
                                        : 'outline'
                                    }
                                    className="text-xs"
                                  >
                                    {activity.hasChildren ? 'Yes' : 'No'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order Info - Horizontal layout */}
                    {activity.nearestOrder ? (
                      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <div className="flex items-center gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Latest Order
                              </p>
                              <p className="text-sm font-medium">
                                {activity.nearestOrder.id}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Date
                              </p>
                              <p className="text-sm">
                                {activity.nearestOrder.date}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Amount
                              </p>
                              <p className="text-sm font-medium">
                                {activity.nearestOrder.amount}
                              </p>
                            </div>
                          </div>
                        </div>
                        <Badge
                          variant={
                            activity.nearestOrder.status === 'completed'
                              ? 'default'
                              : activity.nearestOrder.status === 'shipped'
                                ? 'secondary'
                                : activity.nearestOrder.status === 'confirmed'
                                  ? 'default'
                                  : 'outline'
                          }
                          className="text-xs"
                        >
                          {activity.nearestOrder.status}
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-muted/20 rounded-lg border">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          No previous orders
                        </span>
                      </div>
                    )}

                    {/* Interested Products - Compact table-like layout */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-4 py-2 border-b">
                        <div className="flex items-center gap-2">
                          <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            Interested Products (
                            {activity.interestedProducts.length})
                          </span>
                        </div>
                      </div>
                      <div className="divide-y">
                        {activity.interestedProducts.map(
                          (product, productIndex) => (
                            <Dialog key={productIndex}>
                              <DialogTrigger asChild>
                                <div
                                  className="flex items-center gap-4 p-3 hover:bg-muted/50 cursor-pointer transition-colors group"
                                  onClick={() =>
                                    setSelectedProduct(
                                      productDetails[product.name] ?? {
                                        id: product.sku || product.name,
                                        name: product.name,
                                        price: product.price || '',
                                        category: product.category,
                                        description: product.description || '',
                                        image: product.image,
                                        specifications: {
                                          dimensions: '',
                                          material: '',
                                          colors: [],
                                          warranty: '',
                                        },
                                        availability: '',
                                        rating: 0,
                                        reviews: 0,
                                        location: product.origin || '',
                                      }
                                    )
                                  }
                                >
                                  {/* Product Image - Smaller */}
                                  <div className="w-20 h-16 flex-shrink-0 overflow-hidden rounded bg-muted">
                                    <ImageWithFallback
                                      src={product.image}
                                      alt={product.name}
                                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    />
                                  </div>

                                  {/* Product Info - Horizontal */}
                                  <div className="flex-1 flex items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="text-sm font-medium group-hover:text-primary transition-colors truncate">
                                        {product.name}
                                      </h4>
                                      <Badge
                                        variant="secondary"
                                        className="text-xs mt-1"
                                      >
                                        {product.category}
                                      </Badge>
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-medium text-green-600 whitespace-nowrap">
                                        {product.price}
                                      </span>
                                      <Eye className="h-4 w-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                                    </div>
                                  </div>
                                </div>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle className="flex items-center space-x-2">
                                    <Package className="h-5 w-5" />
                                    <span>Product Details</span>
                                  </DialogTitle>
                                </DialogHeader>
                                {selectedProduct && (
                                  <div className="space-y-6">
                                    {/* Product Image */}
                                    <div className="flex justify-center">
                                      <ImageWithFallback
                                        src={selectedProduct.image}
                                        alt={selectedProduct.name}
                                        className="w-full h-64 object-cover rounded-lg"
                                      />
                                    </div>

                                    {/* Product Info */}
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-lg">
                                          {selectedProduct.name}
                                        </h3>
                                        <div className="text-right">
                                          <p className="font-bold text-xl text-green-600">
                                            {selectedProduct.price}
                                          </p>
                                          <Badge
                                            variant="secondary"
                                            className="text-xs"
                                          >
                                            {selectedProduct.category}
                                          </Badge>
                                        </div>
                                      </div>

                                      <p className="text-muted-foreground line-clamp-3">
                                        {selectedProduct.description}
                                      </p>

                                      {/* Rating and Reviews */}
                                      <div className="flex items-center space-x-4">
                                        <div className="flex items-center space-x-1">
                                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                          <span className="font-medium">
                                            {selectedProduct.rating}
                                          </span>
                                          <span className="text-muted-foreground">
                                            ({selectedProduct.reviews} reviews)
                                          </span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <MapPin className="h-4 w-4 text-muted-foreground" />
                                          <span className="text-sm text-muted-foreground">
                                            {selectedProduct.location}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Availability */}
                                      <div className="flex items-center space-x-2">
                                        <Badge
                                          variant={
                                            selectedProduct.availability.includes(
                                              'Low'
                                            )
                                              ? 'destructive'
                                              : 'default'
                                          }
                                          className="text-xs"
                                        >
                                          {selectedProduct.availability}
                                        </Badge>
                                      </div>

                                      {/* Specifications */}
                                      <div className="border rounded-lg p-4">
                                        <h4 className="font-medium mb-3">
                                          Specifications
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                          <div>
                                            <span className="font-medium">
                                              Dimensions:
                                            </span>
                                            <p className="text-muted-foreground">
                                              {
                                                selectedProduct.specifications
                                                  .dimensions
                                              }
                                            </p>
                                          </div>
                                          <div>
                                            <span className="font-medium">
                                              Material:
                                            </span>
                                            <p className="text-muted-foreground">
                                              {
                                                selectedProduct.specifications
                                                  .material
                                              }
                                            </p>
                                          </div>
                                          <div>
                                            <span className="font-medium">
                                              Available Colors:
                                            </span>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {selectedProduct.specifications.colors.map(
                                                (color, index) => (
                                                  <Badge
                                                    key={index}
                                                    variant="outline"
                                                    className="text-xs"
                                                  >
                                                    {color}
                                                  </Badge>
                                                )
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <span className="font-medium">
                                              Warranty:
                                            </span>
                                            <p className="text-muted-foreground">
                                              {
                                                selectedProduct.specifications
                                                  .warranty
                                              }
                                            </p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Actions */}
                                      <div className="flex space-x-2 pt-2">
                                        <Button size="sm" className="flex-1">
                                          View in Products
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="flex-1"
                                        >
                                          Share Details
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
